import { type ComponentProps } from 'react';
import styled from 'styled-components';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';

import DragHandle from 'components/shared/DragHandle';
import Menu from 'components/shared/menu';
import { simpleDate } from 'lib/date';
import { DischargeChecklistItem } from 'models/DischargeChecklistItem';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';
import TrashIcon from 'svg/TrashIcon';

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

type Props = {
  items: DischargeChecklistItem[];
  canEdit: boolean;
  // Off while searching: positions come from the whole list, so a drag inside a filtered one would
  // land somewhere the user can't see.
  canReorder: boolean;
  // Two overlapping saves can land in either order, so no drag starts until the last one is back. A
  // drop that was ignored instead would leave the row where dnd-kit put it, looking saved.
  isSavingOrder: boolean;
  onMove: (fromIndex: number, toIndex: number) => void;
  onEdit: (item: DischargeChecklistItem) => void;
  onDelete: (item: DischargeChecklistItem) => void;
};

export default function DischargeChecklistTable({
  items,
  canEdit,
  canReorder,
  isSavingOrder,
  onMove,
  onEdit,
  onDelete,
}: Props) {
  const handleDragEnd: DragEndHandler = (event) => {
    if (event.canceled) return;
    const { source } = event.operation;

    if (!source || !isSortable(source)) return;
    onMove(source.initialIndex, source.index);
  };

  return (
    <Table data-testid='discharge-checklist-table'>
      <HeaderRow>
        {canReorder && <HandleCell />}
        <HeaderCell>Item</HeaderCell>
        <HeaderCell>Last Updated</HeaderCell>
        {canEdit && <MenuCell />}
      </HeaderRow>
      {items.length === 0 ? (
        <Row>
          <Cell style={{ justifyContent: 'center' }}>No checklist items</Cell>
        </Row>
      ) : (
        <DragDropProvider onDragEnd={handleDragEnd}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              item={item}
              index={index}
              canEdit={canEdit}
              canReorder={canReorder}
              isSavingOrder={isSavingOrder}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </DragDropProvider>
      )}
    </Table>
  );
}

function SortableRow({
  item,
  index,
  canEdit,
  canReorder,
  isSavingOrder,
  onEdit,
  onDelete,
}: {
  item: DischargeChecklistItem;
  index: number;
  canEdit: boolean;
  canReorder: boolean;
  isSavingOrder: boolean;
  onEdit: (item: DischargeChecklistItem) => void;
  onDelete: (item: DischargeChecklistItem) => void;
}) {
  // `disabled`, not just a missing handle: with no handle registered, dnd-kit makes the whole row the
  // drag source.
  const { ref, handleRef, isDragSource } = useSortable({ id: item.id, index, disabled: !canReorder || isSavingOrder });

  return (
    <Row ref={ref} style={{ opacity: isDragSource ? 0.5 : 1 }} data-testid='discharge-checklist-row'>
      {canReorder && (
        <HandleCell>
          <DragHandle ref={handleRef} data-testid='discharge-checklist-drag-handle' />
        </HandleCell>
      )}
      <Cell>
        <Label>{item.label}</Label>
      </Cell>
      <Cell>
        <BodySmall>
          {simpleDate(item.updatedAt)}
          {item.updatedByName && ` by ${item.updatedByName}`}
        </BodySmall>
      </Cell>
      {canEdit && (
        <MenuCell>
          <Menu>
            <Menu.Trigger>
              <MenuTriggerButton aria-label='Checklist item actions' data-testid='discharge-checklist-row-menu'>
                <EllipsisIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
              </MenuTriggerButton>
            </Menu.Trigger>
            <Menu.Content position='right' style={{ padding: 0, minWidth: 220 }}>
              <Menu.Item onClick={() => onEdit(item)}>
                <Menu.Icon>
                  <PencilIcon width={16} height={16} />
                </Menu.Icon>
                Edit checklist item
              </Menu.Item>
              <Menu.Item onClick={() => onDelete(item)}>
                <Menu.Icon>
                  <TrashIcon width={16} height={16} color={colors.red100} />
                </Menu.Icon>
                <span style={{ color: colors.red100 }}>Delete checklist item</span>
              </Menu.Item>
            </Menu.Content>
          </Menu>
        </MenuCell>
      )}
    </Row>
  );
}

const Table = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;

  > * {
    border-bottom: 1px solid ${colors.black10};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.white};
`;

const HeaderRow = styled(Row)`
  background-color: transparent;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 16px 24px;
  overflow-wrap: anywhere;
`;

const HeaderCell = styled(Cell)`
  font-weight: var(--font-weight-medium);
  color: ${colors.black50};
`;

const HandleCell = styled.div`
  flex: 0 0 56px;
  display: flex;
  justify-content: center;
`;

const MenuCell = styled.div`
  flex: 0 0 56px;
  display: flex;
  justify-content: center;
`;

const Label = styled(BodySmall)`
  font-weight: var(--font-weight-medium);
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.black50};
  border-radius: 4px;

  &:hover {
    background-color: ${colors.black5};
  }
`;
